import { useState, useCallback } from 'react';

const STORAGE_KEY_PREFIX = 'quiklearn_tutorial_video_seen_';

/**
 * Hook to manage showing/hiding of the tutorial video modal (multi-step).
 * Uses localStorage to persist "has seen" state per user.
 *
 * @param {string} userId - Optional user ID for per-user tracking
 */
export function useTutorialVideo(userId) {
    const [isTutorialVideoOpen, setIsTutorialVideoOpen] = useState(false);

    const storageKey = userId ? `${STORAGE_KEY_PREFIX}${userId}` : STORAGE_KEY_PREFIX;

    const hasSeenTutorialVideo = useCallback(() => {
        try {
            return localStorage.getItem(storageKey) === 'true';
        } catch (e) {
            console.warn('[useTutorialVideo] localStorage access failed', e);
            return false;
        }
    }, [storageKey]);

    const markTutorialVideoSeen = useCallback(() => {
        try {
            localStorage.setItem(storageKey, 'true');
        } catch (e) {
            console.warn('[useTutorialVideo] localStorage write failed', e);
        }
    }, [storageKey]);

    const showTutorialVideo = useCallback(() => {
        setIsTutorialVideoOpen(true);
    }, []);

    const showTutorialVideoIfNotSeen = useCallback(() => {
        if (!hasSeenTutorialVideo()) {
            setIsTutorialVideoOpen(true);
        }
    }, [hasSeenTutorialVideo]);

    const hideTutorialVideo = useCallback(() => {
        setIsTutorialVideoOpen(false);
        // Mark as seen when user closes the modal
        markTutorialVideoSeen();
    }, [markTutorialVideoSeen]);

    return {
        isTutorialVideoOpen,
        showTutorialVideo,
        showTutorialVideoIfNotSeen,
        hideTutorialVideo,
        hasSeenTutorialVideo,
    };
}

export default useTutorialVideo;
